import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  TextField,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Group as GroupIcon,
  Event as EventIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useGroups } from '../contexts/GroupContext';
import ProfilePictureDisplay from '../components/ProfilePictureUpload';
import PageContainer from '../components/PageContainer';

export default function ProfilePage() {
  const { currentUser, userProfile, updateUserProfile, loading } = useAuth();
  const { groups } = useGroups();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editForm, setEditForm] = useState({
    displayName: '',
    bio: '',
    phoneNumber: '',
    preferences: {
      notifications: true,
      emailUpdates: true,
      privacy: 'private'
    }
  });

  // Initialize form with current user data
  useEffect(() => {
    if (currentUser && userProfile) {
      setEditForm({
        displayName: currentUser.displayName || userProfile.displayName || '',
        bio: userProfile.bio || '',
        phoneNumber: userProfile.phoneNumber || '',
        preferences: {
          notifications: userProfile.preferences?.notifications ?? true,
          emailUpdates: userProfile.preferences?.emailUpdates ?? true,
          privacy: userProfile.preferences?.privacy ?? 'private'
        }
      });
    }
  }, [currentUser, userProfile]);

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');

    // Format based on length
    if (phoneNumber.length === 0) return '';
    if (phoneNumber.length <= 3) return `(${phoneNumber}`;
    if (phoneNumber.length <= 6) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    if (phoneNumber.length <= 10) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;

    // For longer numbers (like international), just format the first 10 digits
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  // Handle phone number input changes with formatting
  const handlePhoneNumberChange = (value) => {
    const formatted = formatPhoneNumber(value);
    handleInputChange('phoneNumber', formatted);
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing - reset form to current values
      setEditForm({
        displayName: currentUser?.displayName || userProfile?.displayName || '',
        bio: userProfile?.bio || '',
        phoneNumber: userProfile?.phoneNumber || '',
        preferences: {
          notifications: userProfile?.preferences?.notifications ?? true,
          emailUpdates: userProfile?.preferences?.emailUpdates ?? true,
          privacy: userProfile?.preferences?.privacy ?? 'private'
        }
      });
    }
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateUserProfile(editForm);
      setIsEditing(false);
      setSnackbar({
        open: true,
        message: 'Profile updated successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update profile. Please try again.',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setEditForm(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setEditForm(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // User stats - Updated to use real-time groups data
  const userStats = {
    groups: groups.length, // Use real-time groups count instead of userProfile.groups
    parties: userProfile?.parties?.length || 0,
    memberSince: userProfile?.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently',
    lastUpdated: userProfile?.updatedAt?.toDate?.()?.toLocaleDateString() || 'Recently'
  };

  // Show loading state while profile is being fetched
  if (loading) {
    return (
      <PageContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <Typography variant="h6">Loading profile...</Typography>
        </Box>
      </PageContainer>
    );
  }

  // Show message if no user or profile data
  if (!currentUser) {
    return (
      <PageContainer>
        <Typography variant="h4" color="error">Please sign in to view your profile</Typography>
      </PageContainer>
    );
  }

  if (!userProfile) {
    return (
      <PageContainer>
        <Typography variant="h4" color="warning">Profile is being created...</Typography>
        <Typography variant="body1">Please wait while we set up your profile.</Typography>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          My Profile
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Manage your profile information and preferences.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Combined Profile Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 3
            }}>
              <Typography variant="h6">Profile Information</Typography>
              <Button
                variant={isEditing ? "outlined" : "contained"}
                startIcon={isEditing ? <CancelIcon /> : <EditIcon />}
                onClick={handleEditToggle}
                disabled={saving}
              >
                {isEditing ? 'Cancel' : 'Edit Profile'}
              </Button>
            </Box>

            <Grid container spacing={3}>
              {/* Profile Picture and Basic Info Section */}
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                  {/* Profile Picture */}
                  <ProfilePictureDisplay
                    currentPhotoURL={currentUser.photoURL || userProfile.photoURL}
                    displayName={currentUser.displayName || userProfile.displayName}
                    email={currentUser.email}
                  />

                  {/* Basic Info - Display Name, Email, Phone */}
                  <Box sx={{ flex: 1 }}>
                    {isEditing ? (
                      <>
                        <TextField
                          fullWidth
                          label="Display Name"
                          value={editForm.displayName}
                          onChange={(e) => handleInputChange('displayName', e.target.value)}
                          variant="outlined"
                          size="small"
                          sx={{ mb: 1 }}
                          required
                        />
                        <TextField
                          fullWidth
                          label="Email"
                          value={currentUser.email}
                          variant="outlined"
                          size="small"
                          sx={{ mb: 1 }}
                          disabled
                          helperText="Email cannot be changed"
                        />
                        <TextField
                          fullWidth
                          label="Phone Number"
                          value={editForm.phoneNumber}
                          onChange={(e) => handlePhoneNumberChange(e.target.value)}
                          variant="outlined"
                          size="small"
                          placeholder="+1 (555) 123-4567"
                        />
                        <Box sx={{ mt: 2 }}>
                          <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saving}
                            size="small"
                            fullWidth
                          >
                            {saving ? 'Saving...' : 'Save Changes'}
                          </Button>
                        </Box>
                      </>
                    ) : (
                      <>
                        <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                          {currentUser.displayName || userProfile.displayName || 'User'}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" gutterBottom>
                          {currentUser.email}
                        </Typography>
                        {userProfile?.phoneNumber && (
                          <Typography variant="body2" color="text.secondary">
                            📞 {userProfile.phoneNumber}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                </Box>

                {/* Stats Section */}
                <Box sx={{ mt: 3, width: '100%' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'primary.50' }}>
                        <GroupIcon sx={{ fontSize: 24, color: 'primary.main', mb: 1 }} />
                        <Typography variant="h5" color="primary">
                          {userStats.groups}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Groups
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'secondary.50' }}>
                        <EventIcon sx={{ fontSize: 24, color: 'secondary.main', mb: 1 }} />
                        <Typography variant="h5" color="secondary">
                          {userStats.parties}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Parties
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'success.50' }}>
                        <CalendarIcon sx={{ fontSize: 20, color: 'success.main', mb: 1 }} />
                        <Typography variant="body2" color="success.main">
                          {userStats.memberSince}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Member Since
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'info.50' }}>
                        <CalendarIcon sx={{ fontSize: 20, color: 'info.main', mb: 1 }} />
                        <Typography variant="body2" color="info.main">
                          {userStats.lastUpdated}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Last Updated
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Profile Information and Editing Section */}
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Bio"
                      value={editForm.bio}
                      onChange={(e) => handleInputChange('bio', e.target.value)}
                      variant="outlined"
                      multiline
                      rows={5}
                      placeholder="Tell us about yourself..."
                      disabled={!isEditing}
                      sx={{ minWidth: '100%' }}
                    />
                  </Grid>
                </Grid>

                {/* Preferences Section */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Preferences
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth disabled={!isEditing}>
                        <InputLabel>Privacy Level</InputLabel>
                        <Select
                          value={editForm.preferences.privacy}
                          label="Privacy Level"
                          onChange={(e) => handleInputChange('preferences.privacy', e.target.value)}
                        >
                          <MenuItem value="private">Private</MenuItem>
                          <MenuItem value="friends">Friends Only</MenuItem>
                          <MenuItem value="public">Public</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={editForm.preferences.notifications}
                              onChange={(e) => handleInputChange('preferences.notifications', e.target.checked)}
                              disabled={!isEditing}
                            />
                          }
                          label="Push Notifications"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={editForm.preferences.emailUpdates}
                              onChange={(e) => handleInputChange('preferences.emailUpdates', e.target.checked)}
                              disabled={!isEditing}
                            />
                          }
                          label="Email Updates"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Save Button - Removed since it's now in the profile picture section */}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Coming Soon Features */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              🚀 Coming Soon
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              In the next features, you'll be able to:
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                Connect social media accounts
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Set custom notification sounds
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Export your party planning data
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Set availability preferences
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}
